import React, { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import CloseIcon from "@mui/icons-material/Close";
import BarChartIcon from "@mui/icons-material/BarChart";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useUser } from "../../../../users/providers/UserProvider";
import useCategories from "../../../../cards/hooks/useCategories";

const NAVBAR_HEIGHT_TOP = 108;
const NAVBAR_HEIGHT_SCROLLED = 76;

const MoreButton = () => {
  const [open, setOpen] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showCategorySubmenu, setShowCategorySubmenu] = useState(false);
  const [selectedParentCategory, setSelectedParentCategory] =
    useState<any>(null);
  const [atTop, setAtTop] = useState(true);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const navigate = useNavigate();
  const { user } = useUser();
  const { categories, loading: categoriesLoading } = useCategories();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY === 0);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => {
    setOpen(false);
    setShowAdminMenu(false);
    setShowCategorySubmenu(false);
    setSelectedParentCategory(null);
  };

  const handleCategoryClick = (category: any) => {
    const hasChildren = category.children && category.children.length > 0;

    if (hasChildren) {
      // Show submenu for children
      setSelectedParentCategory(category);
      setShowCategorySubmenu(true);
    } else {
      // Navigate directly
      navigate(`/category/${category.name}`);
      handleDrawerClose();
    }
  };

  const handleChildCategoryClick = (childName: string) => {
    navigate(`/category/${childName}`);
    handleDrawerClose();
  };

  const handleAdminMenuClick = () => {
    setShowAdminMenu(true);
  };

  const handleBackToMainMenu = () => {
    setShowAdminMenu(false);
  };

  const handleBackToCategoriesMenu = () => {
    setShowCategorySubmenu(false);
    setSelectedParentCategory(null);
  };

  const handleAdminItemClick = (route: string) => {
    navigate(route);
    handleDrawerClose();
    setAdminMenuAnchor(null);
  };

  const handleDesktopAdminMenuClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleDesktopAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  const navHeight = atTop ? NAVBAR_HEIGHT_TOP : NAVBAR_HEIGHT_SCROLLED;

  return (
    <>
      {/* Mobile Menu Button */}
      <Box sx={{ display: { xs: "inline-flex", md: "none" } }}>
        <IconButton
          size="large"
          color="inherit"
          aria-label="menu"
          sx={{
            display: { xs: "inline-flex", md: "none" },
            transition: "transform 0.3s",
          }}
          onClick={open ? handleDrawerClose : handleDrawerOpen}
        >
          <Box sx={{ position: "relative", width: 32, height: 32 }}>
            <MenuIcon
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                opacity: open ? 0 : 1,
                transform: open ? "rotate(90deg) scale(0.7)" : "none",
                transition: "all 0.3s",
                fontSize: 32,
              }}
            />
            <CloseIcon
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                opacity: open ? 1 : 0,
                transform: open
                  ? "rotate(0deg) scale(1)"
                  : "rotate(-90deg) scale(0.7)",
                transition: "all 0.3s",
                fontSize: 32,
              }}
            />
          </Box>
        </IconButton>
        <Drawer
          anchor="right"
          open={open}
          onClose={handleDrawerClose}
          PaperProps={{
            sx: {
              width: "100vw",
              top: `${navHeight}px`,
              height: `calc(100vh - ${navHeight}px)`,
              bgcolor: "#ffffff",
              borderTop: "1px solid #e0e0e0",
            },
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {/* Main Menu Content */}
            {!showAdminMenu && !showCategorySubmenu && (
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                <List sx={{ pt: 2 }}>
                  {/* Admin Section */}
                  {user?.isAdmin && (
                    <>
                      <ListItem disablePadding sx={{ width: "100%" }}>
                        <ListItemText
                          primary="ניהול מערכת"
                          sx={{
                            textAlign: "right",
                            fontSize: "1.2rem",
                            fontWeight: 600,
                            color: "#047ABC",
                            px: 2,
                            py: 1,
                          }}
                        />
                      </ListItem>
                      <ListItem disablePadding sx={{ width: "100%" }}>
                        <ListItemButton
                          onClick={handleAdminMenuClick}
                          sx={{ pr: 2, pl: 1 }}
                        >
                          <ListItemText
                            primary="ניהול מערכת"
                            sx={{
                              textAlign: "right",
                              fontSize: "1.1rem",
                              color: "#222",
                            }}
                          />
                          <ListItemIcon
                            sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                          >
                            <SettingsIcon fontSize="small" />
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    </>
                  )}
                  {/* Categories Section */}
                  <>
                    <Divider sx={{ my: 2 }} />
                    <ListItem disablePadding sx={{ width: "100%" }}>
                      <ListItemText
                        primary="קטגוריות"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.2rem",
                          fontWeight: 600,
                          color: "#047ABC",
                          px: 2,
                          py: 1,
                        }}
                      />
                    </ListItem>
                    {categoriesLoading ? (
                      <ListItem disablePadding sx={{ width: "100%" }}>
                        <ListItemButton sx={{ pr: 2, pl: 1 }}>
                          <ListItemText
                            primary="טוען קטגוריות..."
                            sx={{
                              textAlign: "right",
                              fontSize: "1.1rem",
                              color: "#666",
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ) : categories &&
                      Array.isArray(categories) &&
                      categories.length > 0 ? (
                      categories.map((category) => (
                        <ListItem
                          key={category._id}
                          disablePadding
                          sx={{ width: "100%" }}
                        >
                          <ListItemButton
                            onClick={() => handleCategoryClick(category)}
                            sx={{ pr: 2, pl: 1 }}
                          >
                            <ListItemText
                              primary={category.name}
                              sx={{
                                textAlign: "right",
                                fontSize: "1.1rem",
                                color: "#222",
                              }}
                            />
                            <ListItemIcon
                              sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                            >
                              <ArrowBackIosNewIcon fontSize="small" />
                            </ListItemIcon>
                          </ListItemButton>
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disablePadding sx={{ width: "100%" }}>
                        <ListItemText
                          primary="אין קטגוריות זמינות"
                          sx={{ textAlign: "right", px: 2, color: "#666" }}
                        />
                      </ListItem>
                    )}
                  </>
                </List>
              </Box>
            )}

            {/* Admin Menu Content */}
            {showAdminMenu && (
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {/* Admin Header with back button */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconButton onClick={handleBackToMainMenu} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#047ABC",
                      fontWeight: "bold",
                    }}
                  >
                    ניהול מערכת
                  </Typography>
                </Box>

                {/* Admin Menu Items */}
                <List sx={{ pt: 1 }}>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/create-card")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="add products/הוסף מוצר"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <AddCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/categories")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="ניהול קטגוריות"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <img
                          src="/assets/icons/category.svg"
                          alt="Categories"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/coupons")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="ניהול קופונים"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <img
                          src="/assets/icons/coupon.svg"
                          alt="Coupon"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/marketing")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="ניהול שיווק"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <img
                          src="/assets/icons/window.svg"
                          alt="Marketing"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/customers")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="ניהול לקוחות"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/orders")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="ניהול הזמנות"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <img
                          src="/assets/icons/order.svg"
                          alt="Orders"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/sent-orders")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="הזמנות שנשלחו"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <img
                          src="/assets/icons/order.svg"
                          alt="Sent Orders"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() => handleAdminItemClick("/admin/analytics")}
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="ניתוח נתונים"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <BarChartIcon fontSize="small" />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ width: "100%" }}>
                    <ListItemButton
                      onClick={() =>
                        handleAdminItemClick("/admin/all-products")
                      }
                      sx={{ pr: 2, pl: 1 }}
                    >
                      <ListItemText
                        primary="כל המוצרים"
                        sx={{
                          textAlign: "right",
                          fontSize: "1.1rem",
                          color: "#222",
                        }}
                      />
                      <ListItemIcon
                        sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                      >
                        <img
                          src="/assets/icons/category.svg"
                          alt="All Products"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                </List>
              </Box>
            )}

            {/* Category Submenu Content - Shows children of selected category */}
            {showCategorySubmenu && selectedParentCategory && (
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {/* Submenu Header with back button */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    onClick={handleBackToCategoriesMenu}
                    sx={{ mr: 1 }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#047ABC",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedParentCategory.name}
                  </Typography>
                </Box>

                {/* Child Categories List */}
                <List sx={{ pt: 1 }}>
                  {selectedParentCategory.children?.map((child: any) => (
                    <ListItem
                      key={child._id}
                      disablePadding
                      sx={{ width: "100%" }}
                    >
                      <ListItemButton
                        onClick={() => handleChildCategoryClick(child.name)}
                        sx={{ pr: 2, pl: 1 }}
                      >
                        <ListItemText
                          primary={child.name}
                          sx={{
                            textAlign: "right",
                            fontSize: "1.1rem",
                            color: "#222",
                          }}
                        />
                        <ListItemIcon
                          sx={{ minWidth: 0, pl: 2, color: "#047ABC" }}
                        >
                          <ArrowBackIosNewIcon fontSize="small" />
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Social Media Section - Only show in main menu */}
            {!showAdminMenu && !showCategorySubmenu && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: user?.isAdmin ? 5 : 10,
                  width: "100%",
                  bgcolor: "#f9f9f9",
                  pb: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "#666",
                    mb: 2,
                    fontSize: "0.9rem",
                  }}
                >
                  עקבו אחרינו
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  <IconButton
                    href="https://www.facebook.com/profile.php?id=61586699859793"
                    sx={{
                      color: "#1877f2",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    href="https://www.instagram.com/lev.haaretz/"
                    sx={{
                      color: "#e4405f",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        </Drawer>
      </Box>

      {/* Desktop Admin Menu - Only show on large screens when user is admin */}
      {isLargeScreen && user?.isAdmin && (
        <Box sx={{ display: { xs: "none", md: "inline-flex" } }}>
          <IconButton
            size="large"
            color="inherit"
            aria-label="admin menu"
            onClick={handleDesktopAdminMenuClick}
            sx={{
              color: "#113355",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <SettingsIcon />
          </IconButton>
          <Menu
            anchorEl={adminMenuAnchor}
            open={Boolean(adminMenuAnchor)}
            onClose={handleDesktopAdminMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1,
                },
              },
            }}
          >
            <MenuItem onClick={() => handleAdminItemClick("/create-card")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AddCircleOutlineIcon fontSize="small" />
              </ListItemIcon>
              הוסף מוצר
            </MenuItem>
            <MenuItem onClick={() => handleAdminItemClick("/admin/categories")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <img
                  src="/assets/icons/category.svg"
                  alt="Categories"
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              ניהול קטגוריות
            </MenuItem>
            <MenuItem onClick={() => handleAdminItemClick("/admin/coupons")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <img
                  src="/assets/icons/coupon.svg"
                  alt="Coupon"
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              ניהול קופונים
            </MenuItem>
            <MenuItem onClick={() => handleAdminItemClick("/admin/marketing")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <img
                  src="/assets/icons/window.svg"
                  alt="Marketing"
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              ניהול שיווק
            </MenuItem>
            <MenuItem onClick={() => handleAdminItemClick("/admin/customers")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              ניהול לקוחות
            </MenuItem>
            <MenuItem onClick={() => handleAdminItemClick("/admin/orders")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <img
                  src="/assets/icons/order.svg"
                  alt="Orders"
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              ניהול הזמנות
            </MenuItem>
            <MenuItem
              onClick={() => handleAdminItemClick("/admin/sent-orders")}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <img
                  src="/assets/icons/order.svg"
                  alt="Sent Orders"
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              הזמנות שנשלחו
            </MenuItem>
            <MenuItem onClick={() => handleAdminItemClick("/admin/analytics")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BarChartIcon fontSize="small" />
              </ListItemIcon>
              ניתוח נתונים
            </MenuItem>
            <MenuItem
              onClick={() => handleAdminItemClick("/admin/all-products")}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <img
                  src="/assets/icons/category.svg"
                  alt="All Products"
                  style={{ width: 20, height: 20 }}
                />
              </ListItemIcon>
              כל המוצרים
            </MenuItem>
          </Menu>
        </Box>
      )}
    </>
  );
};

export default MoreButton;
